import {
  tab as markdownTab,
  tabset as markdownTabset,
} from '@squareup/dex-ui-shared-markdown-components';
import React, {
  Children,
  FC,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  isValidElement,
  useMemo,
} from 'react';

interface MdxTabProps {
  title: string;
  children?: React.ReactNode;
}

const MdxTab: FC<PropsWithChildren<MdxTabProps>> = ({ children }) => {
  // Tab contents are rendered by the parent Tabs component
  return <>{children}</>;
};

MdxTab.displayName = 'MdxTab';

const MarkdownTabset = markdownTabset.component.value as FC<{
  tabsMetadata: Array<{ id: string; label: string; contents?: ReactNode }>;
}>;
const MarkdownTab = markdownTab.component.value as FC<
  PropsWithChildren<{ id: string }>
>;

const MdxTabs: FC<PropsWithChildren> = ({ children }) => {
  // Render through Contentful's own tabset components rather than reassembling
  // the tab strip here. Both pipelines then emit the same class names, which the
  // docs stylesheet keys spacing off — `.tabset + .tab-content + heading` gives a
  // heading that follows a tabset 8x of clearance, and a hand-rolled tab strip
  // silently loses it (headings landed 24px too high on every migrated page with
  // a tabset).
  //
  // Build the tab list once per `children` change; recomputing it on every render
  // feeds TabAnimator a fresh `tabs` prop and makes its content swap behave
  // unpredictably. Memoising stabilises every downstream identity.
  const tabsMetadata = useMemo(() => {
    const collected: Array<{
      id: string;
      label: string;
      contents: ReactNode;
    }> = [];
    Children.forEach(children, (child, index) => {
      if (!isValidElement(child)) {
        return;
      }
      const props = child.props as Record<string, unknown>;
      const title = (props.title as string) || `Tab ${index + 1}`;
      // Include index so duplicate titles (or titles that slug to the same
      // string, e.g. special-char-only) still produce unique tab ids within
      // one <Tabs> group.
      const id = `${index}-${title.toLowerCase().replace(/\s+/g, '-')}`;
      collected.push({
        id,
        label: title,
        contents: (
          <MarkdownTab id={title}>
            {(child as ReactElement).props.children}
          </MarkdownTab>
        ),
      });
    });
    return collected;
  }, [children]);

  if (tabsMetadata.length === 0) {
    return null;
  }

  return <MarkdownTabset tabsMetadata={tabsMetadata} />;
};

MdxTabs.displayName = 'MdxTabs';

export { MdxTabs, MdxTab };
